import { Trade } from '../../types';

const normalize = (s: unknown) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

export const getStartOfLocalDayMs = (dateValue?: string): number | null => {
  const raw = String(dateValue ?? '').trim();
  if (!raw) return null;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]) - 1;
    const day = Number(isoMatch[3]);
    const localDate = new Date(year, month, day, 0, 0, 0, 0);
    return Number.isFinite(localDate.getTime()) ? localDate.getTime() : null;
  }

  const parsed = new Date(dateValue as any);
  if (!Number.isFinite(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 0, 0, 0, 0).getTime();
};

export const getEndOfLocalDayMs = (dateValue?: string): number | null => {
  const start = getStartOfLocalDayMs(dateValue);
  return start == null ? null : start + (24 * 60 * 60 * 1000) - 1;
};

export const getTradeTimestampMs = (trade: Trade | any): number | null => {
  const openValue =
    (trade as any).openTimestampMs ??
    (trade as any).timestamp ??
    (trade as any).entryTime ??
    (trade as any).openTime ??
    (trade as any).time ??
    (trade as any).date;
  if (openValue == null) return null;
  const parsed = new Date(openValue as any);
  return parsed && Number.isFinite(parsed.getTime()) ? parsed.getTime() : null;
};

export const matchesTradeSearch = (trade: Trade | any, rawQuery: string): boolean => {
  const query = String(rawQuery ?? '').trim();
  if (!query) return true;
  const tokens = normalize(query).split(/\s+/).filter(Boolean);

  const tags = Array.isArray((trade as any).tags) ? (trade as any).tags.join(' ') : String((trade as any).tags ?? '');
  const idParts = [
    (trade as any).tradeId,
    (trade as any).orderId,
    (trade as any).id,
  ].map((v) => String(v ?? '')).filter(Boolean).join(' ');
  const sourceIds = Array.isArray((trade as any).sourceIds) ? (trade as any).sourceIds.join(' ') : '';
  const symbol = String((trade as any).symbol ?? '');
  const symbolNoVenue = symbol.includes(':') ? symbol.split(':').pop() || symbol : symbol;

  const haystack = [
    symbol,
    symbolNoVenue,
    (trade as any).notes,
    tags,
    String((trade as any).tradeId ?? ''),
    idParts,
    sourceIds,
  ]
    .map((v) => normalize(v))
    .join(' ');

  return tokens.every((tok) => haystack.includes(tok));
};

export type TradesPageFilters = {
  timeframe: 'alltime' | 'last24h' | '7days' | '30days' | 'custom';
  fromDate?: string;
  toDate?: string;
  sideFilter?: 'All' | 'Long' | 'Short';
  searchTerm?: string;
  advancedFilters?: any;
};

export const matchesTimeframeFilter = (trade: Trade | any, filters: TradesPageFilters): boolean => {
  if (filters.timeframe === 'alltime') return true;

  const ts = getTradeTimestampMs(trade);
  if (ts == null) return false;

  if (filters.timeframe === 'last24h') return ts >= Date.now() - (24 * 60 * 60 * 1000);
  if (filters.timeframe === '7days') return ts >= Date.now() - (7 * 24 * 60 * 60 * 1000);
  if (filters.timeframe === '30days') return ts >= Date.now() - (30 * 24 * 60 * 60 * 1000);

  // For custom ranges require both fromDate and toDate to be present; otherwise do not filter.
  if (filters.timeframe === 'custom') {
    const hasFrom = Boolean(String(filters.fromDate ?? '').trim());
    const hasTo = Boolean(String(filters.toDate ?? '').trim());
    if (!hasFrom || !hasTo) return true;
  }

  const startMs = getStartOfLocalDayMs(filters.fromDate);
  const endMs = getEndOfLocalDayMs(filters.toDate);
  if (startMs == null && endMs == null) return true;

  const lower = startMs != null && endMs != null ? Math.min(startMs, endMs) : startMs;
  const upper = startMs != null && endMs != null ? Math.max(startMs, endMs) : endMs;

  if (lower != null && ts < lower) return false;
  if (upper != null && ts > upper) return false;
  return true;
};
